<template>
<div class="tnm-training-application-list">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="培訓隊名單申請清單"
      subtitle="審議與管理各協會培訓隊名單申請"
      icon="tabler-users-group"
      :count="data.length"
    >
      <template #actions>
        <VBtn
          color="white"
          variant="flat"
          class="text-primary font-weight-bold"
          :disabled="tableSelect.length === 0"
          :loading="viewParams.dwnLoading"
          @click="exportData"
        >
          <VIcon icon="tabler-download" class="mr-1" />匯出
        </VBtn>
      </template>
    </ListPageHeader>

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.applyNo"
            label="申請單號"
            clearable
            prepend-inner-icon="tabler-search"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
          prepend-inner-icon="tabler-search"
          no-data-text="尚無資料"
          v-model="filters.projectId"
          :items="projectSelect"
          label="所屬計畫項目"
          item-value="value"
          item-title="title"
          clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
          prepend-inner-icon="tabler-search"
          no-data-text="尚無資料"
          v-model="filters.deptNo"
          :items="deptNoSelect"
          label="協會"
          item-value="value"
          item-title="title"
          clearable
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="filters.status" :items="statusSelect" label="狀態" item-value="value" item-title="title" clearable></v-select>
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :items-length="data.length"
      :items="pagedItems"
      :headers="headers"
      :loading="loading"
      :row-clickable="false"
      :row-props="rowProps"
      show-select
      v-model="tableSelect"
      :item-disabled="itemDisabled"
      :item-selectable="itemDisabled"
      item-value="applyNo"
      empty-text="尚無培訓隊名單申請"
      empty-subtitle="可調整上方篩選條件後再查詢"
      empty-icon="tabler-users-group"
      :items-per-page-options="[
        {value: 10, title: '10'},
        {value: 25, title: '25'},
        {value: 50, title: '50'},
        {value: 100, title: '100'},
      ]"
    >
      <template #item.applyNo="{ item }">
        <div class="d-inline-flex align-center flex-nowrap">
          <v-btn
            @click.stop="review(item)"
            color="primary"
            variant="text"
            class="pa-0 idt-link font-weight-bold"
          >
            {{ item.applyNo }}
          </v-btn>
          <v-tooltip location="top" text="複製單號">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                variant="text"
                icon
                size="x-small"
                density="compact"
                class="ms-1"
                @click.stop="notifyCopyDocNo(item.applyNo)"
              >
                <VIcon icon="tabler-copy" size="18" />
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </template>

      <template #item.deptNo="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">{{ viewParams.deptNoMapping[item.deptNo] }}</span>
          <span class="text-caption text-medium-emphasis">{{ item.deptNo }}</span>
        </div>
      </template>

      <template #item.projectId="{ item }">
        <span class="text-high-emphasis">{{ viewParams.projectTitleMapping[item.projectId] }}</span>
      </template>

      <template #item.applyTime="{ item }">
        <span v-if="item.applyTime" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
          <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
          {{ convertToROCDate(item.applyTime,'-') }}
        </span>
        <span v-else class="text-disabled">—</span>
      </template>

      <template v-slot:item.stage="{ item }">
        {{ stageToText(item.stage) }}
      </template>

      <template v-slot:item.status="{ item }">
        <Status
          :status="item.status"
          :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
          :is-green="['Resubmitted','Passed']"
          :is-warning="['Draft','ReturnedForRevision']"
          :shared-table-id="tabId"
          btn-class="x-large"
          font-class="18px"
          :status-config="getStatusData"
        />
      </template>
    </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Status from './StatusComponenet.vue'
import { useRouter } from "vue-router"
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
const router = useRouter()
const fileUrl = useStorage('drFsUrl').value

  definePage({
    meta: {
      action: 'manage',
      subject: 'TRAININGTEAMLIST',
    },
  })

  const viewParams = reactive({
    projectTitleMapping: {},
    deptNoMapping: {},
    dwnLoading: false
  });

  const filters = reactive({
    applyNo: null,
    projectId: null,
    deptNo: null,
    status: null
  });

  const headers = ref([
    { title: '申請單號', key: 'applyNo', icon: 'tabler-file-text' },
    { title: '單位', key: 'deptNo', icon: 'tabler-building-community' },
    { title: '計畫項目', key: 'projectId', icon: 'tabler-folder' },
    { title: '申請日期', key: 'applyTime', icon: 'tabler-calendar-event' },
    { title: '階段', key: 'stage', icon: 'tabler-stairs' },
    { title: '狀態', key: 'status', icon: 'tabler-flag' },
  ]);

  const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('tnm_training_application_list_last_clicked', 'applyNo')

  const tableSelect = ref([]);
  // const tableSelect = reactive({
  //   checkedApplyNo: new Set(),
  //   // checkedApplyNo = [],
  //   select: (item, event)=> {
  //     if(event.target.checked) {
  //       tableSelect.checkedApplyNo.add(item.applyNo);
  //     } else {
  //       tableSelect.checkedApplyNo.delete(item.applyNo);
  //     }
  //   },
  // });
  
  // const selectAllToggle = (props) =>{
  //   if(this.selected.length != this.desserts.length - this.disabledCount) {
  //     this.selected = [];
  //     const self = this;
  //     props.items.forEach(item => {
  //       if(!item.disabled) {
  //         self.selected.push(item);
  //       } 
  //     });
  //   } else this.selected = [];
  // }

  const projectSelect = ref([]);
  const statusSelect = ref([]);
  const deptNoSelect = ref([]);
  const getSelectList = async () => {
    await $api('/tca/trainingteamlist/get/select', {
      method: 'GET',
    }).then((response) => {

      projectSelect.value = response.data.projectSelect.map(([k, v]) => ({
        title: v,
        value: k
      }));
      projectSelect.value.forEach(select => {
        viewParams.projectTitleMapping[select.value] = select.title;
      });

      statusSelect.value = response.data.statusSelect.map(([k, v]) => ({
        title: v,
        value: k
      }));

      deptNoSelect.value = response.data.deptNoSelect.map(([k, v]) => ({
        title: v,
        value: k
      }));
      deptNoSelect.value.forEach(select => {
        viewParams.deptNoMapping[select.value] = select.title;
      });

    }).catch((error) => {
      console.error('取得下拉選單失敗:', error);
    });
  };

  const page = ref(1)
  const itemsPerPage = ref(100)
  const totalData = ref(0);
  const data = ref([]);
  const loading = ref(false);

  // client 端分頁切片（資料一次抓回，前端分頁）
  const pagedItems = computed(() => {
    if (itemsPerPage.value === -1) return data.value
    const start = (page.value - 1) * itemsPerPage.value

    return data.value.slice(start, start + itemsPerPage.value)
  })

  // JWT
  const getJ = async()=>{
    const { data: jwt, execute: getJwt } = await useApi<any>(createUrl('/tca/file/get', {})) 
    return jwt
  }
  const fetchData = async () => {
    const projectId = filters.projectId ? filters.projectId : null;
    const deptNo = filters.deptNo ? filters.deptNo : null;
    const status = filters.status ? filters.status : null;
    const applyNo = filters.applyNo ? filters.applyNo : null;

    page.value = 1;
    loading.value = true;
    await $api('/tca/trainingteamlist/search', {
      method: 'POST',
      body: {
        projectId : projectId,
        deptNo : deptNo,
        status : status,
        applyNo : applyNo,
        page : 0,
        size : 10000
      }
    }).then((response) => {
      data.value = response.data;
      totalData.value = response.data.length;
    }).catch((error) => {
      console.error('查詢失敗:', error);
    }).finally(() => {
      loading.value = false;
    });
  };

  const exportData = async(item: any) => {
    viewParams.dwnLoading = true;
    const apiUrl='/tca/trainingteamlist/exportData'
    await $api(apiUrl, {
      method: 'POST',
      body: Array.from(tableSelect.value)
    }).then(async(resp) => {
      viewParams.dwnLoading = false;
      if (resp.statusCode == 200) {
        const url = await mapFileDownload(fileUrl, resp.data)
        window.open(url, '_blank');
      } else  {
        popSwal('error','匯出失敗,錯誤訊息'+resp.data);
      }
    })
  };

  const itemDisabled = (item) => {
    return item.status == 'Passed';
  }

  const review = (item: any) => {
    setLastClickedId(item.applyNo)
    router.push({ path: '/tca/tnm/TrainingTeamApplication', query: {
       applyNo: item.applyNo, 
    } })
  };

  const notifyCopyDocNo = async (text: string | null | undefined) => {
    const ok = await copyPlainText(text)
    $swal({
      icon: ok ? 'success' : 'error',
      title: ok ? '已複製到剪貼簿' : '複製失敗',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: ok ? 1500 : 2000,
    })
  };

  const popSwal = (icon,title) => {
  $swal({
    position: 'top-end',
    icon: icon,
    title: title,
    showConfirmButton: false,
    timer: 3000,
    customClass: {
      confirmButton: 'btn btn-primary',
    },
    buttonsStyling: false,
  });
}

  watch(filters, () => {
    fetchData();
  })

  onMounted(() => {
    getSelectList();
    fetchData();
  })

const stageToText = (stage) => {
  // 轉換 第一階段：1，第二階段第一期：2.1 ，以此類推
  let dispStage = '';
  const regex = /(\d+)\.?(\d?)/;
  const matches = stage.match(regex);
  if(matches) {
    let cNum = numberToChinese(matches[1]);
    dispStage = '第' + cNum + '階段';
    if(matches[2]) {
      cNum = numberToChinese(matches[2]);
      dispStage = dispStage + '第' + cNum +'期';
    }
  } else {
    dispStage = '建議參賽名單';
  }
  return dispStage;
};

function numberToChinese(num) {
  const chineseDigits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  let chineseNum = '';
  // 处理整数部分
  const integerPart = Math.floor(num);
  if (integerPart > 0) {
    const digits = integerPart.toString().split('').reverse();
    for (let i = 0; i < digits.length; i++) {
      if (digits[i] !== '0') {
        chineseNum = chineseDigits[digits[i]] +  chineseNum;
      } else {
        // 处理连续的零
        if (chineseNum.length > 0 && chineseNum[chineseNum.length - 1] !== '零') {
          chineseNum += '零';
        }
      }
    }
  }
  return chineseNum;
}

const tabId = ref('TrainingTeamApplication')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

</script>


<style scoped>
/* Add your styles here */
</style>
